// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import InteractionResponses from '@app/features/application_commands/state/InteractionResponses';
import {createSystemMessage} from '@app/features/devtools/utils/CommandUtils';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import {TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {replyDescriptor} from '@app/features/messaging/utils/MessageComponentReplies';
import {http} from '@app/features/platform/transport/RestTransport';
import {failureMessage} from '@app/features/platform/utils/ResponseInspection';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {MessageFlags} from '@fluxer/constants/src/ChannelConstants';
import type {MessageComponentClickResponse} from '@fluxer/schema/src/domains/message/MessageComponentSchemas';
import {fromTimestampWithSequence, SnowflakeSequence} from '@fluxer/snowflake/src/SnowflakeUtils';
import type {I18n} from '@lingui/core';

export interface PendingComponentInteraction {
	id: string;
	status: string;
	deadline_at: string;
}

type ClickResponse = MessageComponentClickResponse & {interaction?: PendingComponentInteraction | null};

const nonceSequence = new SnowflakeSequence();

function nextNonce(): string {
	if (nonceSequence.willOverflowNext()) nonceSequence.reset();
	return fromTimestampWithSequence(Date.now(), nonceSequence);
}

export async function clickMessageComponent(
	i18n: I18n,
	message: Message,
	customId: string,
	values?: ReadonlyArray<string>,
): Promise<PendingComponentInteraction | null> {
	const sessionId = GatewayConnection.sessionId;
	// A button on a private reply is resolved by the server from the interaction that produced it.
	const sourceInteractionId = message.hasFlag(MessageFlags.EPHEMERAL) ? message.interactionMetadata?.id : undefined;
	let body: ClickResponse;
	try {
		const response = await http.post<ClickResponse>(
			Endpoints.CHANNEL_MESSAGE_COMPONENTS(message.channelId, message.id),
			{
				body: {
					custom_id: customId,
					...(values ? {values} : {}),
					...(sessionId ? {session_id: sessionId} : {}),
					...(sourceInteractionId ? {source_interaction_id: sourceInteractionId} : {}),
					nonce: nextNonce(),
				},
			},
		);
		body = response.body;
	} catch (error) {
		ToastCommands.error(failureMessage(error) ?? i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
		return null;
	}
	const descriptor = body.reply ? replyDescriptor(body.reply.code) : undefined;
	if (descriptor) {
		// Shown only on this client, authored by the bot that owns the button; the server keeps no copy.
		const local = createSystemMessage(message.channelId, i18n._(descriptor), message.author.toJSON());
		MessageCommands.createOptimistic(message.channelId, local.toJSON());
	}
	if (body.interaction) {
		InteractionResponses.track({
			interaction: body.interaction,
			channelId: message.channelId,
			author: message.author.toJSON(),
			metadata: null,
			showPlaceholder: false,
			sessionId: sessionId ?? null,
		});
	}
	return body.interaction ?? null;
}
